import React, { useMemo } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import ReactApexChart from 'react-apexcharts';
import { merge } from 'lodash';
import { BaseOptionChart } from './';
import ChartErrorBoundary from '../ChartErrorBoundary';

function bucket(hours) {
  // round to nearest half-hour for grouping
  if (typeof hours !== 'number' || isNaN(hours)) return 'unknown';
  return Math.round(hours * 2) / 2; // e.g., 14.25 -> 14.5
}

export default function UserSelectedTimeHistogram({ data = [] }) {
  const counts = useMemo(() => {
    const acc = {};
    data.forEach(d => {
      const val = d.userSelectedTime;
      const b = bucket(typeof val === 'string' ? parseFloat(val) : val);
      acc[b] = (acc[b] || 0) + 1;
    });
    return acc;
  }, [data]);

  const labels = Object.keys(counts).filter(k => k !== 'unknown').sort((a, b) => parseFloat(a) - parseFloat(b));
  const series = [{ name: 'Selections', data: labels.map(l => counts[l]) }];

  const options = merge(BaseOptionChart(), {
    chart: { type: 'bar' },
    plotOptions: { bar: { columnWidth: '60%' } },
    xaxis: { categories: labels, title: { text: 'Hour of Day (rounded to 0.5h)' } },
    yaxis: { title: { text: 'Count' } }
  });

  if (!options.colors || !Array.isArray(options.colors) || options.colors.length === 0) {
    options.colors = ['#1976d2', '#00acc1'];
  }

  const hasData = labels && labels.length > 0;
  const renderSeries = hasData ? series : [{ name: 'No data', data: [0] }];
  const renderOptions = { ...options, xaxis: { ...(options.xaxis || {}), categories: hasData ? labels : ['No data'] } };
  // tooltip: show count and bucket
  renderOptions.tooltip = renderOptions.tooltip || {};
  renderOptions.tooltip.y = renderOptions.tooltip.y || {};
  renderOptions.tooltip.y.formatter = (val) => (typeof val === 'number' ? `${val} selections` : val);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography component="h3" variant="subtitle2" gutterBottom>
          User Selected Time Distribution
        </Typography>
        {hasData ? (
          <ChartErrorBoundary>
            <ReactApexChart options={renderOptions} series={renderSeries} type="bar" height={260} />
          </ChartErrorBoundary>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No data to display</Typography>
        )}
      </CardContent>
    </Card>
  );
}
